import React, { useState, useRef, useCallback } from 'react';
import { Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertCircle, X, Loader2 } from 'lucide-react';
import { uploadAPI } from '../services/api';

const MAX_SIZE_MB = 10;
const ALLOWED_EXT = ['.xlsx', '.xls'];
const ALLOWED_MIME = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
];

function validateFile(file) {
  if (!file) return 'Please select a file.';
  const ext = '.' + file.name.split('.').pop().toLowerCase();
  if (!ALLOWED_EXT.includes(ext) && !ALLOWED_MIME.includes(file.type)) {
    return 'Invalid file type. Only .xlsx and .xls files are accepted.';
  }
  if (file.size > MAX_SIZE_MB * 1024 * 1024) {
    return `File is too large. Maximum size is ${MAX_SIZE_MB} MB.`;
  }
  return null;
}

const UploadExcel = () => {
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [status, setStatus] = useState(null); // null | 'uploading' | 'success' | 'error'
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [rowsInserted, setRowsInserted] = useState(null);
  const [validationError, setValidationError] = useState('');
  const inputRef = useRef(null);

  const reset = () => {
    setFile(null);
    setStatus(null);
    setProgress(0);
    setMessage('');
    setRowsInserted(null);
    setValidationError('');
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleFileSelect = (selected) => {
    setStatus(null);
    setMessage('');
    setRowsInserted(null);
    const err = validateFile(selected);
    if (err) {
      setValidationError(err);
      setFile(null);
      return;
    }
    setValidationError('');
    setFile(selected);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileSelect(dropped);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = () => setDragOver(false);

  const handleInputChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) handleFileSelect(selected);
  };

  const handleUpload = async () => {
    if (!file) return;
    const err = validateFile(file);
    if (err) { setValidationError(err); return; }

    setStatus('uploading');
    setProgress(0);
    setMessage('');
    setRowsInserted(null);

    try {
      const res = await uploadAPI.uploadExcel(file, (evt) => {
        if (evt.total) {
          setProgress(Math.round((evt.loaded / evt.total) * 100));
        }
      });
      setStatus('success');
      setMessage(res.data.message || 'Upload successful.');
      setRowsInserted(res.data.rowsInserted ?? null);
    } catch (err) {
      setStatus('error');
      setMessage(
        err.response?.data?.message ||
        'Upload failed. Please try again.'
      );
    }
  };

  const fileSizeFmt = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white">Upload Excel</h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Upload an Excel file (.xlsx / .xls) to insert all rows directly into Google Sheets.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => !file && inputRef.current?.click()}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer
          ${dragOver
            ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/20'
            : file
              ? 'border-brand-400 bg-brand-50/50 dark:bg-brand-900/10 cursor-default'
              : 'border-gray-300 dark:border-gray-700 hover:border-brand-400 dark:hover:border-brand-600 hover:bg-gray-50 dark:hover:bg-gray-800/50'
          }
        `}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,.xls"
          className="hidden"
          onChange={handleInputChange}
        />

        {file ? (
          <div className="flex items-center gap-4 justify-center">
            <div className="w-12 h-12 bg-brand-100 dark:bg-brand-900/40 rounded-xl flex items-center justify-center flex-shrink-0">
              <FileSpreadsheet className="w-6 h-6 text-brand-600 dark:text-brand-400" />
            </div>
            <div className="text-left min-w-0">
              <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{file.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{fileSizeFmt(file.size)}</p>
            </div>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); reset(); }}
              className="ml-auto p-1.5 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0"
              title="Remove file"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-2xl flex items-center justify-center mx-auto">
              <Upload className="w-7 h-7 text-gray-400 dark:text-gray-500" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Drag & drop your file here, or{' '}
                <span className="text-brand-600 dark:text-brand-400 underline underline-offset-2">browse</span>
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">.xlsx or .xls · Max {MAX_SIZE_MB} MB</p>
            </div>
          </div>
        )}
      </div>

      {/* Validation error */}
      {validationError && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
          <AlertCircle className="w-4 h-4 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-700 dark:text-amber-300">{validationError}</p>
        </div>
      )}

      {/* Upload progress */}
      {status === 'uploading' && (
        <div className="space-y-2">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span className="flex items-center gap-1.5">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Uploading…
            </span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-brand-600 h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      )}

      {/* Success message */}
      {status === 'success' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">{message}</p>
            {rowsInserted !== null && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-0.5">
                {rowsInserted} row{rowsInserted !== 1 ? 's' : ''} inserted into Google Sheet.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Error message */}
      {status === 'error' && (
        <div className="flex items-start gap-3 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700 dark:text-red-300">{message}</p>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={handleUpload}
          disabled={!file || status === 'uploading'}
          className="flex items-center gap-2 px-5 py-2.5 bg-brand-600 hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors duration-150"
        >
          {status === 'uploading' ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Uploading…</>
          ) : (
            <><Upload className="w-4 h-4" /> Upload Excel</>
          )}
        </button>

        {(file || status) && status !== 'uploading' && (
          <button
            type="button"
            onClick={reset}
            className="px-4 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
          >
            Reset
          </button>
        )}
      </div>

      {/* Info note */}
      <p className="text-xs text-gray-400 dark:text-gray-600">
        Data is appended after the last existing row in <strong className="font-medium text-gray-500 dark:text-gray-500">sheet1</strong>. The first row of your Excel file is treated as data (not a header) unless it already exists in the sheet.
      </p>
    </div>
  );
};

export default UploadExcel;
